declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
    _fbq?: unknown
  }
}

type MetaPixelEvent =
  | 'AddPaymentInfo'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'CompleteRegistration'
  | 'Contact'
  | 'CustomizeProduct'
  | 'Donate'
  | 'FindLocation'
  | 'InitiateCheckout'
  | 'Lead'
  | 'PageView'
  | 'Purchase'
  | 'Schedule'
  | 'Search'
  | 'StartTrial'
  | 'SubmitApplication'
  | 'Subscribe'
  | 'ViewContent'

interface MetaPixelEventData {
  [key: string]: unknown
}

interface PurchaseEventData extends MetaPixelEventData {
  value: number
  currencyName?: string
  contents?: { id: string; quantity: number }[]
  content_type?: string
}

interface AddToCartEventData extends MetaPixelEventData {
  content_ids: string[]
  content_type: string
  value?: number
  currencyName?: string
}

interface AddPaymentInfoEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
  content_ids?: string[]
  content_type?: string
}

interface AddToWishlistEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
  content_ids?: string[]
  content_type?: string
}

interface CompleteRegistrationEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
  status?: string
}

interface ContactEventData extends MetaPixelEventData {
  content_name?: string
}

interface CustomizeProductEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
}

interface DonateEventData extends MetaPixelEventData {
  value: number
  currencyName?: string
}

interface FindLocationEventData extends MetaPixelEventData {
  store_location_id?: string
}

interface InitiateCheckoutEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
  content_ids?: string[]
  num_items?: number
  content_type?: string
}

interface LeadEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
}

interface ScheduleEventData extends MetaPixelEventData {
  appointment_id?: string
}

interface SearchEventData extends MetaPixelEventData {
  search_string?: string
}

interface StartTrialEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
}

interface SubmitApplicationEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
}

interface SubscribeEventData extends MetaPixelEventData {
  value?: number
  currencyName?: string
}

interface ViewContentEventData extends MetaPixelEventData {
  content_ids?: string[]
  content_type?: string
  value?: number
  currencyName?: string
}

type MetaPixelEventMap = {
  AddPaymentInfo: AddPaymentInfoEventData
  AddToCart: AddToCartEventData
  AddToWishlist: AddToWishlistEventData
  CompleteRegistration: CompleteRegistrationEventData
  Contact: ContactEventData
  CustomizeProduct: CustomizeProductEventData
  Donate: DonateEventData
  FindLocation: FindLocationEventData
  InitiateCheckout: InitiateCheckoutEventData
  Lead: LeadEventData
  PageView: MetaPixelEventData
  Purchase: PurchaseEventData
  Schedule: ScheduleEventData
  Search: SearchEventData
  StartTrial: StartTrialEventData
  SubmitApplication: SubmitApplicationEventData
  Subscribe: SubscribeEventData
  ViewContent: ViewContentEventData
}

type CustomEvents =
  | 'TripCreated'
  | 'PaymentInitiated'
  | 'PaymentInitiateError'
  | 'TripPaid'
  | 'TripCancelled'
  | 'TripCreateError'
  | 'TripPaymentError'
  | 'TripCancelError'
  | 'Error'

interface CustomEventPayloads extends Record<string, unknown> {
  TripCreated: {
    tripId: string
    userId: string
    createdAt: string
  }
  PaymentInitiated: {
    transactionId: string
    amount: number
    currencyName?: string
    tripId: string
  }
  PaymentInitiateError: {
    message: string
    tripId?: string
  }
  TripPaid: {
    tripId: string
    transactionId: string
    amount: number
    currencyName?: string
  }
  TripCancelled: {
    tripId: string
    reason: string
  }
  TripCreateError: {
    message: string
  }
  TripPaymentError: {
    tripId: string
    transactionId?: string
    message: string
  }
  TripCancelError: {
    tripId: string
    message: string
  }
}

export interface IFacebookPixelService {
  pageView: () => void
  track: <T extends MetaPixelEvent>(
    event: T,
    data: MetaPixelEventMap[T],
  ) => void
  trackSingle: <T extends MetaPixelEvent>(
    event: T,
    data: MetaPixelEventMap[T],
  ) => void
  trackCustom: <T extends CustomEvents>(
    eventName: T,
    data: CustomEventPayloads[T],
  ) => void
  trackSingleCustom: <T extends CustomEvents>(
    eventName: T,
    data: CustomEventPayloads[T],
  ) => void
}

export class FacebookPixelService implements IFacebookPixelService {
  pageView(): void {
    this.track('PageView', {})
  }

  track<T extends MetaPixelEvent>(event: T, data: MetaPixelEventMap[T]): void {
    this.handleEvent(() => window.fbq?.('track', event, data))
  }

  trackSingle<T extends MetaPixelEvent>(
    event: T,
    data: MetaPixelEventMap[T],
  ): void {
    this.handleEvent(() => window.fbq?.('trackSingle', event, data))
  }

  trackCustom<T extends CustomEvents>(
    eventName: T,
    data: CustomEventPayloads[T],
  ): void {
    this.handleEvent(() => window.fbq?.('trackCustom', eventName, data))
  }

  trackSingleCustom<T extends CustomEvents>(
    eventName: T,
    data: CustomEventPayloads[T],
  ): void {
    this.handleEvent(() => window.fbq?.('trackSingleCustom', eventName, data))
  }

  private handleEvent(cb: () => void) {
    if (typeof window.fbq === 'function') {
      return cb()
    }
    console.warn('Facebook Pixel is not initialized')
  }
}
